import React, { useState } from 'react';
import {
  ImportArtifactPreview,
  ImportStatePreview,
  ImportProcessPreview,
  ImportTransitionPreview,
} from '../../services/primeArtifactImportService';
import styles from './ImportTreeView.module.css';

interface ImportTreeViewProps {
  artifacts: ImportArtifactPreview[];
  onArtifactSelect?: (artifact: ImportArtifactPreview) => void;
  selectedArtifact?: ImportArtifactPreview | null;
}

interface TreeNodeProps {
  artifact: ImportArtifactPreview;
  level: number;
  onSelect: (artifact: ImportArtifactPreview) => void;
  isSelected: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  artifact,
  level,
  onSelect,
  isSelected,
  isExpanded,
  onToggle,
}) => {
  const hasChildren =
    artifact.states.length > 0 || artifact.processes.length > 0 || artifact.transitions.length > 0;

  return (
    <div className={styles.treeNode}>
      <div
        className={`${styles.treeNodeContent} ${isSelected ? styles.selected : ''} ${styles[artifact.conflict_type] || ''}`}
        style={{ paddingLeft: `${level * 20 + 10}px` }}
        onClick={() => onSelect(artifact)}
      >
        <div className={styles.nodeHeader}>
          {hasChildren && (
            <button
              className={styles.expandButton}
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
          {!hasChildren && <div className={styles.expandSpacer} />}

          <div className={styles.nodeInfo}>
            <span className={styles.nodeName}>{artifact.name}</span>
            <span className={`${styles.conflictBadge} ${styles[artifact.conflict_type] || ''}`}>
              {artifact.conflict_type}
            </span>
          </div>
        </div>

        {artifact.description && <div className={styles.nodeDescription}>{artifact.description}</div>}

        <div className={styles.nodeStats}>
          <span className={styles.stat}>States: {artifact.states.length}</span>
          <span className={styles.stat}>Processes: {artifact.processes.length}</span>
          <span className={styles.stat}>Transitions: {artifact.transitions.length}</span>
        </div>
      </div>

      {isExpanded && hasChildren && (
        <div className={styles.nodeChildren}>
          {artifact.states.length > 0 && (
            <div className={styles.childrenGroup}>
              <div className={styles.groupHeader}>States</div>
              {artifact.states.map((state, index) => (
                <StateNode key={index} state={state} level={level + 1} />
              ))}
            </div>
          )}

          {artifact.processes.length > 0 && (
            <div className={styles.childrenGroup}>
              <div className={styles.groupHeader}>Processes</div>
              {artifact.processes.map((process, index) => (
                <ProcessNode key={index} process={process} level={level + 1} />
              ))}
            </div>
          )}

          {artifact.transitions.length > 0 && (
            <div className={styles.childrenGroup}>
              <div className={styles.groupHeader}>Transitions</div>
              {artifact.transitions.map((transition, index) => (
                <TransitionNode key={index} transition={transition} level={level + 1} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StateNode: React.FC<{ state: ImportStatePreview; level: number }> = ({ state, level }) => (
  <div className={`${styles.childNode} ${styles.stateNode}`} style={{ paddingLeft: `${level * 20 + 10}px` }}>
    <div className={styles.childNodeContent}>
      <span className={styles.childName}>{state.name}</span>
      <span className={`${styles.conflictBadge} ${styles[state.conflict_type] || ''}`}>{state.conflict_type}</span>
      <div className={styles.stateFlags}>
        {state.is_initial && <span className={`${styles.flag} ${styles.initial}`}>Initial</span>}
        {state.is_final && <span className={`${styles.flag} ${styles.final}`}>Final</span>}
      </div>
    </div>
  </div>
);

const ProcessNode: React.FC<{ process: ImportProcessPreview; level: number }> = ({
  process,
  level,
}) => (
  <div className={`${styles.childNode} ${styles.processNode}`} style={{ paddingLeft: `${level * 20 + 10}px` }}>
    <div className={styles.childNodeContent}>
      <span className={styles.childName}>{process.name}</span>
      <span className={`${styles.conflictBadge} ${styles[process.conflict_type] || ''}`}>{process.conflict_type}</span>
      {process.process_type && <span className={styles.processType}>{process.process_type}</span>}
    </div>
  </div>
);

const TransitionNode: React.FC<{ transition: ImportTransitionPreview; level: number }> = ({
  transition,
  level,
}) => (
  <div className={`${styles.childNode} ${styles.transitionNode}`} style={{ paddingLeft: `${level * 20 + 10}px` }}>
    <div className={styles.childNodeContent}>
      <span className={styles.childName}>{transition.name}</span>
      <span className={`${styles.conflictBadge} ${styles[transition.conflict_type] || ''}`}>
        {transition.conflict_type}
      </span>
      <div className={styles.transitionDetails}>
        {transition.from_state && transition.to_state && (
          <span className={styles.transitionFlow}>
            {transition.from_state} → {transition.to_state}
          </span>
        )}
      </div>
    </div>
  </div>
);

export const ImportTreeView: React.FC<ImportTreeViewProps> = ({
  artifacts,
  onArtifactSelect,
  selectedArtifact,
}) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const toggleNode = (artifactName: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(artifactName)) {
      newExpanded.delete(artifactName);
    } else {
      newExpanded.add(artifactName);
    }
    setExpandedNodes(newExpanded);
  };

  const handleArtifactSelect = (artifact: ImportArtifactPreview) => {
    onArtifactSelect?.(artifact);
  };

  return (
    <div className={styles.importTreeView}>
      <div className={styles.treeHeader}>
        <h4>Import Preview</h4>
        <div className={styles.treeLegend}>
          <div className={styles.legendItem}>
            <span className={`${styles.legendColor} ${styles.new}`}></span>
            <span>New</span>
          </div>
          <div className={styles.legendItem}>
            <span className={`${styles.legendColor} ${styles.duplicate}`}></span>
            <span>Duplicate</span>
          </div>
        </div>
      </div>

      <div className={styles.treeContent}>
        {artifacts.map((artifact, index) => (
          <TreeNode
            key={index}
            artifact={artifact}
            level={0}
            onSelect={handleArtifactSelect}
            isSelected={selectedArtifact?.name === artifact.name}
            isExpanded={expandedNodes.has(artifact.name)}
            onToggle={() => toggleNode(artifact.name)}
          />
        ))}
      </div>
    </div>
  );
};
